import { logAccess } from "../../../../common/utils/utils";
import { ProductFamilyAttributes } from "../../../../common/models/product-family.model";
import type { ProductPricing } from "../../../../common/utils/product-pricing";

export class Product {
  private _id?: string;
  private _uuid?: string;
  private _name?: string;
  private _brand?: string;
  private _application_types?: APPLICATION_TYPE[];
  private _type?: string;
  private _productTypeName?: string;
  private _color?: string;
  private _pattern?: string;
  private _productTypeId?: number;
  private _vendor_code?: string;
  private _sku?: string;
  private _description?: string;
  private _sizeName?: string;
  private _isVisible?: boolean;
  private _isDraft?: boolean;
  private _thumbnailPath?: string;
  private _galleryImages?: string[];
  private _width?: number;
  private _length?: number;
  private _height?: number; // Height dimension for 3D products (furniture, etc.)
  private _unit?: string;
  private _link?: any;
  private _addToCartLink?: any;
  // Read-only: what the API resolved for shoppers (price, pack, sample). Never saved back.
  private _pricing?: ProductPricing;
  private _attributes?: ProductAttributes;
  private _formats?: Format[];
  private _visualization?: VisualizationWrapper;
  private _siblings?: Product[];
  private _size?: Size;
  private _isDefault?: boolean;
  private _productColors?: string[];
  private _order?: number;
  private _crop?: { x: number; y: number };
  private _zoom?: number;
  private _plankGroupId: string;
  private _groutLineColor?: string;
  private _groutLineWidth?: number;
  private _customFields?: any;

  // Category fields
  private _categoryIds?: string[];
  private _category?: string; // top-level category slug (e.g. 'rugs')

  // Product Family fields
  private _familyId?: string;
  private _familySlug?: string;
  private _familyAttributes?: ProductFamilyAttributes;

  // Timestamps
  private _createdAt?: string;

  // Dynamic variant axis values - stores all axis values by name
  private _axisValues?: Record<string, any>;

  public prepareProduct = (product): this => {
    // Some saved variants store string-typed fields as { value: '...' }
    // objects (legacy of the V2 axes shape leaking through). Coerce them
    // back to plain strings before assignment so React doesn't try to
    // render an object as children.
    const str = (v: any): string | undefined => {
      if (v == null) return undefined;
      if (typeof v === 'string') return v;
      if (typeof v === 'number') return String(v);
      if (typeof v === 'object' && 'value' in v) return v.value;
      return undefined;
    };
    return this.setId(product._id)
      .setUuid(product.uuid)
      .setName(str(product.name))
      .setBrand(str(product.brand))
      .setApplication_types(product.organization?.applicationTypes)
      .setType(str(product.organization?.productType))
      .setProductTypeName(str(product.productTypeName))
      .setPattern(str(product.axes?.pattern))
      .setColor(str(product.color) ?? str(product.axes?.color) ?? str(product.axes?.label))
      .setProductTypeId(product.productTypeId)
      .setVendor_code(str(product.vendor_code))
      .setSku(str(product.sku))
      .setDescription(str(product.description))
      .setSizeName(str(product.sizeName) || this.buildSizeName(product.axes?.size))
      .setIsVisible(product.isVisible)
      .setIsDraft(product.isDraft ?? false)
      .setThumbnailPath(product.visualization?.thumbnailPath || product.visualization?.surface?.materialPath)
      .setGalleryImages(product.galleryImages)
      .setWidth(product.axes?.size?.width)
      .setLength(product.axes?.size?.height)
      .setHeight(product.height)
      .setUnit(product.axes?.size?.unit)
      .setLink(product.commerce?.link)
      .setAddToCartLink(product.commerce?.addToCartLink)
      .setPricing(product.pricing)
      .setAttributes(product.attributes)
      .setFormats(product.formats)
      .setVisualization(product.visualization)
      .setSiblings(product.siblings?.map(sibling => { return (new Product()).prepareProduct(sibling) }))
      .setSize(product.size)
      .setIsDefault(product.isDefault)
      .setProductColors(product.productColors)
      .setCrop(product.crop ?? { x: 0, y: 0 })
      .setZoom(product.zoom ?? 1)
      .setPlankGroupId(product.plankGroupId)
      .setCustomFields(product.customFields)
      .setCategoryIds(product.categoryIds?.map((id: any) => id?.toString?.() || id) || [])
      .setCategory(product.category)
      .setFamilyId(product.familyId)
      .setFamilySlug(product.familySlug)
      .setFamilyAttributes(product.familyAttributes)
      .setAxisValues(product.axisValues || {})
      .setCreatedAt(product.createdAt)
      .setOrder(product.order)
  }

  public toPlainObject(): object {
    // Start with always-required core fields
    const plainProductObj: Record<string, any> = {
      name: this.name,
      brand: this.brand,
      sku: this.sku,
      isVisible: this.isVisible,
      isDefault: this.isDefault,
      attributes: this.attributes,
      visualization: this.visualization
    };

    // Optional core attributes - only include if they have values
    if (this._description !== undefined && this._description !== null && this._description !== '') {
      plainProductObj.description = this.description;
    }
    if (this._link !== undefined && this._link !== null && this._link !== '') {
      plainProductObj.link = this.link;
    }
    if (this._addToCartLink !== undefined && this._addToCartLink !== null && this._addToCartLink !== '') {
      plainProductObj.addToCartLink = this.addToCartLink;
    }

    // Only include color if set (depends on family's color axis)
    if (this._color !== undefined && this._color !== null) {
      plainProductObj.color = this.color;
    }

    // Only include size-related fields if set (depends on family's size axis)
    if (this._sizeName !== undefined && this._sizeName !== null) {
      plainProductObj.sizeName = this.sizeName;
    }
    if (this._width !== undefined && this._width !== null) {
      plainProductObj.width = Number(this.width);
    }
    if (this._length !== undefined && this._length !== null) {
      plainProductObj.length = Number(this.length);
    }
    if (this._height !== undefined && this._height !== null) {
      plainProductObj.height = Number(this.height);
    }
    if (this._unit !== undefined && this._unit !== null) {
      plainProductObj.unit = this.unit;
    }
    // Only include formats if set (depends on family's size axis)
    if (this._formats && this._formats.length > 0) {
      plainProductObj.formats = this.formats;
    }

    // Only include pattern if set (depends on family's pattern axis)
    if (this._pattern) {
      plainProductObj.pattern = this.pattern;
    }

    // Only include application_types if set (depends on family's visualizationConfig)
    if (this._application_types && this._application_types.length > 0) {
      plainProductObj.application_types = this.application_types;
    }

    // Only include type if set (depends on family's visualizationConfig)
    if (this._type) {
      plainProductObj.type = this.type;
    }

    if (this._id) {
      plainProductObj.id = this._id;
    }

    if (this._uuid) {
      plainProductObj.uuid = this._uuid;
    }

    if (this._plankGroupId) {
      plainProductObj.plankGroupId = this._plankGroupId;
    }

    if (this._crop !== undefined) {
      plainProductObj.crop = this._crop;
    }

    if (this._zoom !== undefined) {
      plainProductObj.zoom = this._zoom;
    }
    
    if (this._groutLineColor !== undefined) {
      plainProductObj.groutLineColor = this._groutLineColor;
    }

    if (this._groutLineWidth !== undefined) {
      plainProductObj.groutLineWidth = this._groutLineWidth;
    }

    // Product Family fields
    if (this._familyId !== undefined) {
      plainProductObj.familyId = this._familyId;
    }

    if (this._familySlug !== undefined) {
      plainProductObj.familySlug = this._familySlug;
    }

    if (this._familyAttributes !== undefined) {
      plainProductObj.familyAttributes = this._familyAttributes;
    }

    // Dynamic axis values - stores all variant axis values by name
    if (this._axisValues !== undefined && Object.keys(this._axisValues).length > 0) {
      plainProductObj.axisValues = this._axisValues;
    }

    return plainProductObj;
  }

  public get uuid(): string | undefined {
    return this._uuid;
  }

  public setUuid(uuid: string): this {
    this._uuid = uuid;
    return this;
  }

  public get id(): string | undefined {
    return this._id;
  }

  public setId(id: string): this {
    this._id = id;
    return this;
  }

  public get name(): string | undefined {
    return this._name;
  }

  public setName(name: string): this {
    this._name = name;
    return this;
  }

  public get order(): number | undefined {
    return this._order;
  }

  public setOrder(order: number): this {
    this._order = order;
    return this;
  }

  public get brand(): string | undefined {
    return this._brand;
  }

  public setBrand(brand: string): this {
    this._brand = brand;
    return this;
  }

  public get productColors(): string[] | undefined {
    return this._productColors;
  }

  public setProductColors(colors: string[]): this {
    this._productColors = colors;
    return this;
  }

  public get application_types(): APPLICATION_TYPE[] | undefined {
    return this._application_types;
  }

  public setApplication_types(application_types: APPLICATION_TYPE[]): this {
    this._application_types = application_types;
    return this;
  }

  public get type(): string | undefined {
    return this._type;
  }

  public setType(type: string): this {
    this._type = type;
    return this;
  }

  public get productTypeName(): string | undefined {
    return this._productTypeName;
  }

  public setProductTypeName(productTypeName: string): this {
    this._productTypeName = productTypeName;
    return this;
  }

  public get color(): string | undefined {
    return this._color;
  }

  public setColor(color: string): this {
    this._color = color;
    return this;
  }

  public get pattern(): string | undefined {
    return this._pattern;
  }

  public setPattern(pattern: string): this {
    this._pattern = pattern;
    return this;
  }

  public get productTypeId(): number | undefined {
    return this._productTypeId;
  }

  public setProductTypeId(productTypeId: number): this {
    this._productTypeId = productTypeId;
    return this;
  }

  public get vendor_code(): string | undefined {
    return this._vendor_code;
  }

  public setVendor_code(vendor_code: string): this {
    this._vendor_code = vendor_code;
    return this;
  }

  public get sku(): string | undefined {
    return this._sku;
  }

  public setSku(sku: string): this {
    this._sku = sku;
    return this;
  }

  public get description(): string | undefined {
    return this._description;
  }

  public setDescription(description: string): this {
    this._description = description;
    return this;
  }

  public get sizeName(): string | undefined {
    return this._sizeName;
  }

  public setSizeName(sizeName: string): this {
    this._sizeName = sizeName;
    return this;
  }

  private buildSizeName(size?: { width?: number; height?: number; depth?: number; unit?: string }): string | undefined {
    if (!size || (!size.width && !size.height)) return undefined;
    const unit = size.unit || 'cm';
    const parts = [size.width, size.height].filter(v => v != null && v > 0);
    if (parts.length === 0) return undefined;
    return `${parts.join('x')} ${unit}`;
  }

  public get isVisible(): boolean | undefined {
    return this._isVisible;
  }

  public setIsVisible(isVisible: boolean): this {
    this._isVisible = isVisible;
    return this;
  }

  public get isDraft(): boolean | undefined {
    return this._isDraft;
  }

  public setIsDraft(isDraft: boolean): this {
    this._isDraft = isDraft;
    return this;
  }

  public get thumbnailPath(): string | undefined {
    return this._thumbnailPath;
  }

  public setThumbnailPath(thumbnailPath: string): this {
    this._thumbnailPath = thumbnailPath;
    return this;
  }

  public get galleryImages(): string[] | undefined {
    return this._galleryImages;
  }

  public setGalleryImages(galleryImages?: string[]): this {
    this._galleryImages = Array.isArray(galleryImages) ? galleryImages : undefined;
    return this;
  }

  public get width(): number | undefined {
    return this._width;
  }

  public setWidth(width: number): this {
    this._width = width;
    return this;
  }

  public get length(): number | undefined {
    return this._length;
  }

  public setLength(length: number): this {
    this._length = length;
    return this;
  }

  public get height(): number | undefined {
    return this._height;
  }

  public setHeight(height: number): this {
    this._height = height;
    return this;
  }

  public get unit(): string | undefined {
    return this._unit;
  }

  public setUnit(unit: string): this {
    this._unit = unit;
    return this;
  }

  public get link(): any {
    return this._link;
  }

  public setLink(link: any): this {
    this._link = link;
    return this;
  }

  public get addToCartLink(): any {
    return this._addToCartLink;
  }

  public setAddToCartLink(link: any): this {
    this._addToCartLink = link;
    return this;
  }

  /** What shoppers see for price, packaging and samples (API-resolved, read-only). */
  public get pricing(): ProductPricing | undefined {
    return this._pricing;
  }

  public setPricing(pricing: ProductPricing | undefined): this {
    this._pricing = pricing ?? undefined;
    return this;
  }

  public get attributes(): ProductAttributes | undefined {
    return this._attributes;
  }

  public setAttributes(attributes: ProductAttributes): this {
    this._attributes = attributes;
    return this;
  }

  public get formats(): Format[] | undefined {
    return this._formats;
  }

  public setFormats(formats: Format[]): this {
    this._formats = formats;
    return this;
  }

  public get visualization(): VisualizationWrapper | undefined {
    return this._visualization;
  }

  public setVisualization(visualization: VisualizationWrapper): this {
    this._visualization = visualization;
    return this;
  }

  public get siblings(): Product[] | undefined {
    return this._siblings;
  }

  public setSiblings(siblings: Product[]): this {
    this._siblings = siblings;
    return this;
  }

  public get size(): Size | undefined {
    return this._size;
  }

  public setSize(size: Size): this {
    this._size = size;
    return this;
  }

  public get isDefault(): boolean | undefined {
    return this._isDefault;
  }

  public setIsDefault(isDefault: boolean): this {
    this._isDefault = isDefault;
    return this;
  }

  public get crop(): { x: number; y: number } | undefined {
    return this._crop;
  }

  public setCrop(crop: { x: number; y: number }): this {
    this._crop = crop;
    return this;
  }

  public get zoom(): number | undefined {
    return this._zoom;
  }

  public setZoom(zoom: number): this {
    this._zoom = zoom;
    return this;
  }

  public get plankGroupId(): string {
    return this._plankGroupId;
  }

  @logAccess
  public setPlankGroupId(plankGroupId: string): this {
    this._plankGroupId = plankGroupId;
    return this;
  }

  public getGroutLineColor(): string {
    return this._groutLineColor;
  }

  public getGroutLineWidth(): number {
    return this._groutLineWidth;
  }

  public setGroutLineColor(color: string): this {
    this._groutLineColor = color;
    return this;
  }

  public setGroutLineWidth(width: number): this {
    this._groutLineWidth = width;
    return this;
  }

  public get customFields(): any | undefined {
    return this._customFields;
  }

  public setCustomFields(customFields: any): this {
    this._customFields = customFields;
    return this;
  }

  // Product Family getters and setters
  public get familyId(): string | undefined {
    return this._familyId;
  }

  public setFamilyId(familyId: string): this {
    this._familyId = familyId;
    return this;
  }

  public get categoryIds(): string[] {
    return this._categoryIds || [];
  }

  public setCategoryIds(categoryIds: string[]): this {
    this._categoryIds = categoryIds;
    return this;
  }

  public get category(): string | undefined {
    return this._category;
  }

  public setCategory(category: string): this {
    this._category = category;
    return this;
  }

  public get familySlug(): string | undefined {
    return this._familySlug;
  }

  public setFamilySlug(familySlug: string): this {
    this._familySlug = familySlug;
    return this;
  }

  public get familyAttributes(): ProductFamilyAttributes | undefined {
    return this._familyAttributes;
  }

  public setFamilyAttributes(familyAttributes: ProductFamilyAttributes): this {
    this._familyAttributes = familyAttributes;
    return this;
  }

  /**
   * Check if this product belongs to the Surface Products family
   * (backwards compatible - treats undefined familySlug as surface products)
   */
  public isSurfaceProduct(): boolean {
    return !this._familySlug || this._familySlug === 'surface-products';
  }

  public get createdAt(): string | undefined {
    return this._createdAt;
  }

  public setCreatedAt(createdAt: string): this {
    this._createdAt = createdAt;
    return this;
  }

  // Dynamic axis values getter and setter
  public get axisValues(): Record<string, any> | undefined {
    return this._axisValues;
  }

  public setAxisValues(axisValues: Record<string, any>): this {
    this._axisValues = axisValues;
    return this;
  }

  public getAxisValue(axisName: string): any {
    return this._axisValues?.[axisName];
  }

  public setAxisValue(axisName: string, value: any): this {
    if (!this._axisValues) {
      this._axisValues = {};
    }
    this._axisValues[axisName] = value;
    return this;
  }

}

export enum APPLICATION_TYPE {
  FLOOR = "floor",
  WALLS = "walls",
  CEILING = "ceiling",
}

export interface ProductAttributes {
  styles?: string[]
  collections?: string[]
  categories?: string[]
  colors?: any[]
  features?: string[]
  [key: string]: any // Allow dynamic custom field properties
}

export class Size {
  width?: number
  height?: number
  depth?: number // Third dimension for 3D products (height in physical terms)
  unit = "cm"

  public setWidth(width: number): this {
    this.width = width;
    return this;
  }
  public setHeight(height: number): this {
    this.height = height;
    return this;
  }
  public setDepth(depth: number): this {
    this.depth = depth;
    return this;
  }
}

export interface Format {
  type: string
  name: string
}

export interface VisualizationWrapper {
  data: Visualization[]
}

export interface Visualization {
  type?: string
  code?: string
  is_default?: boolean
  material?: string
  materialPath?: string
  materialPaths?: string[];
  width?: number
  height?: number
  groutLineColor?: string
  groutLineWidth?: number
}
